
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery, useMutation } from "@tanstack/react-query";
import "../Style/Provisioning.css";
import Deviceassociate from "./DeviceAssociate";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// --- API Helper Functions ---

// 1. UPDATED: Returns Objects { id, name } instead of just strings
const fetchCustomers = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  const response = await fetch(`${API_BASE_URL}/customers`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.Message || "Failed to fetch customer list");
  }
  const data = await response.json();
  console.log(data);

  // Map backend keys to frontend friendly keys
  return data.map((customer) => ({
    id: customer.CustomerID,
    name: customer.CustomerName,
  }));
};

const registerDevice = async (payload) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  const response = await fetch(`${API_BASE_URL}/vehicle&deviceRegistration`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.Message || "Error occurred.");
  return data;
};

const onboardDevice = async (payload) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  // Backend expects: device_id, imei, imsi, sim, customer_id
  const backendPayload = {
    imei: payload.imei,
    imsi: payload.imsi,
    iccid: payload.iccid,
    device_type: payload.deviceType,
    version: payload.version,
    customerid: payload.customerId,
  };

  const response = await fetch(`${API_BASE_URL}/deviceRegistrations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(backendPayload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.Message || data.Error || "Failed.");
  return data;
};

const onboardVehicle = async (payload) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  // Backend expects: vehicle_id, vin, customer_id
  const backendPayload = {
    vehicleid: payload.registrationNumber,
    vim: payload.vim,
    customerid: payload.customerId,
    registration_type: payload.registrationType,
    powertrain: payload.powertrain,
    vehicle_type: payload.vehicleType,
    range_km: payload.range,
    operating_hours: payload.hours,
  };

  const response = await fetch(`${API_BASE_URL}/vehicleRegistration`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(backendPayload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.Message || data.Error || "Failed.");
  return data;
};

// --- Skeleton Component ---
function ProvisioningFormSkeleton() {
  const skeletonItems = Array.from({ length: 7 }, (_, i) => i);
  return (
    <>
      <div className="form-grid-Provisioning">
        {skeletonItems.map((item) => (
          <div className="form-group-Provisioning" key={item}>
            <div
              className="skeleton-Provisioning skeleton-label-Provisioning"
              style={{ marginBottom: "8px" }}
            ></div>
            <div className="skeleton-Provisioning skeleton-input-Provisioning"></div>
          </div>
        ))}
      </div>
      <div className="button-container-Provisioning">
        <div className="skeleton-Provisioning skeleton-button-Provisioning"></div>
        <div className="skeleton-Provisioning skeleton-button-Provisioning"></div>
      </div>
    </>
  );
}

// --- Main Component ---
export default function Provisioning() {
  const [activeTab, setActiveTab] = useState("onboard");

  // Registration Tab State
  // const [deviceid, setDeviceid] = useState("");
  const [imei, setImei] = useState("");
  const [imsi, setImsi] = useState("");
  const [customerid, setCustomerid] = useState("");
  const [vim, setVim] = useState("");
  const [iccid, setIccid] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [version, setVersion] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [registrationType, setRegistrationType] = useState("");
  const [powertrain, setPowertrain] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [range, setRange] = useState("");
  const [hours, setHours] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Onboard Device Tab State
  const [onboardIccid, setOnboardIccid] = useState("");
  const [onboardImei, setOnboardImei] = useState("");
  const [onboardImsi, setOnboardImsi] = useState("");
  const [onboardDeviceType, setOnboardDeviceType] = useState("");
  const [onboardVersion, setOnboardVersion] = useState("");
  const [onboardCustomerId, setOnboardCustomerId] = useState("");
  const [onboardMessage, setOnboardMessage] = useState("");
  const [isOnboardError, setIsOnboardError] = useState(false);

  // Onboard Vehicle Tab State
  const [onboardRegistrationNumber, setOnboardRegistrationNumber] =
    useState("");
  const [onboardVim, setOnboardVim] = useState("");
  const [onboardVehicleCustomerId, setOnboardVehicleCustomerId] = useState("");
  const [onboardRegistrationType, setOnboardRegistrationType] = useState("");
  const [onboardVehiclePowertrain, setOnboardVehiclePowertrain] = useState("");
  const [onboardVehicleType, setOnboardVehicleType] = useState("");
  const [onboardVehicleRange, setOnboardVehicleRange] = useState("");
  const [onboardVehicleHours, setOnboardVehicleHours] = useState("");
  const [onboardVehicleMessage, setOnboardVehicleMessage] = useState("");
  const [isOnboardVehicleError, setIsOnboardVehicleError] = useState(false);

  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate("/Deviceassociate");
  };

  const {
    data: customerList = [],
    isLoading: isLoadingCustomers,
    isError: isCustomerError,
  } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: submitRegistration, isLoading: isSubmitting } = useMutation({
    mutationFn: registerDevice,
    onMutate: () => {
      setMessage("");
      setIsError(false);
    },
    onSuccess: (data) => {
      setMessage(data.message || "Registration Successful!");
      setIsError(false);
      // setDeviceid("");
      setImei("");
      setImsi("");
      setCustomerid("");
      setVim("");
      setIccid("");
      setDeviceType("");
      setVersion("");
      setRegistrationNumber("");
      setRegistrationType("");
      setHours("");
      setRange("");
      setVehicleType("");
      setPowertrain("");
    },
    onError: (error) => {
      setMessage(error.message);
      setIsError(true);
    },
  });

  const { mutate: submitOnboarding, isLoading: isOnboarding } = useMutation({
    mutationFn: onboardDevice,
    onMutate: () => {
      setOnboardMessage("");
      setIsOnboardError(false);
    },
    onSuccess: (data) => {
      setOnboardMessage(data.message || "Onboarding Successful!");
      setIsOnboardError(false);
      setOnboardIccid("");
      setOnboardImei("");
      setOnboardImsi("");
      setOnboardDeviceType("");
      setOnboardVersion("");
      setOnboardCustomerId("");
    },
    onError: (error) => {
      setOnboardMessage(error.message);
      setIsOnboardError(true);
    },
  });

  const { mutate: submitVehicleOnboarding, isLoading: isVehicleOnboarding } =
    useMutation({
      mutationFn: onboardVehicle,
      onMutate: () => {
        setOnboardVehicleMessage("");
        setIsOnboardVehicleError(false);
      },
      onSuccess: (data) => {
        setOnboardVehicleMessage(
          data.message || "Vehicle Onboarding Successful!",
        );
        setIsOnboardVehicleError(false);
        // setOnboardVehicleId("");
        setOnboardRegistrationNumber("");
        setOnboardVim("");
        setOnboardVehicleCustomerId("");
        setOnboardRegistrationType("");
        setOnboardVehiclePowertrain("");
        setOnboardVehicleType("");
        setOnboardVehicleRange("");
        setOnboardVehicleHours("");
      },
      onError: (error) => {
        setOnboardVehicleMessage(error.message);
        setIsOnboardVehicleError(true);
      },
    });

  const handleSubmit = () => {
    // customerid state already holds the ID now, not the name
    submitRegistration({
      imei,
      imsi,
      iccid,
      device_type: deviceType,
      version,
      vehicleid: registrationNumber,
      vim,
      customerid,
      registration_type: registrationType,
      powertrain,
      vehicle_type: vehicleType,
      range_km: range,
      operating_hours: hours,
    });
  };

  const handleOnboardSubmit = () => {
    // onboardCustomerId state already holds the ID now
    submitOnboarding({
      iccid: onboardIccid,
      imei: onboardImei,
      imsi: onboardImsi,
      deviceType: onboardDeviceType,
      version: onboardVersion,
      customerId: onboardCustomerId,
    });
  };

  const handleVehicleOnboardSubmit = () => {
    // onboardVehicleCustomerId state already holds the ID now
    submitVehicleOnboarding({
      // vehicleId: onboardVehicleId,
      registrationNumber: onboardRegistrationNumber,
      vim: onboardVim,
      customerId: onboardVehicleCustomerId,
      registrationType: onboardRegistrationType,
      powertrain: onboardVehiclePowertrain,
      vehicleType: onboardVehicleType,
      range: onboardVehicleRange,
      hours: onboardVehicleHours,
    });
  };

  return (
    <div className="container-Provisioning">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="motion-wrapper-Provisioning"
      >
        <header className="header-Provisioning">
          <h1 className="header-title-Provisioning">
            Provisioning Configuration
          </h1>
          <p className="header-subtitle-Provisioning">
            Configure device parameters over the air securely and efficiently.
          </p>
          <hr className="line-Provisioning" width="100%" />
        </header>

        <div className="card-Provisioning">
          <div className="tab-container-Provisioning">
            <button
              className={`tab-button-Provisioning ${activeTab === "onboard" ? "active" : ""}`}
              onClick={() => setActiveTab("onboard")}
            >
              Onboard Device
            </button>
            <button
              className={`tab-button-Provisioning ${activeTab === "onboardVehicle" ? "active" : ""}`}
              onClick={() => setActiveTab("onboardVehicle")}
            >
              Onboard Vehicle
            </button>
            <button
              className={`tab-button-Provisioning ${activeTab === "register" ? "active" : ""}`}
              onClick={() => setActiveTab("register")}
            >
              Device-Vehicle Association
            </button>
          </div>

          <div className="card-content-Provisioning">
            {isLoadingCustomers ? (
              <ProvisioningFormSkeleton />
            ) : isCustomerError ? (
              <div
                style={{ color: "red", textAlign: "center", padding: "20px" }}
              >
                Error: Could not load customer data.
              </div>
            ) : (
              <>
                {/* --- TAB 1: DEVICE REGISTRATION --- */}
                {activeTab === "register" && (
                  <>
                    <h2 className="title-Provisioning">
                      Device-Vehicle Association
                    </h2>
                    <div className="form-grid-Provisioning">
                      {/* UPDATED SELECT: Shows Name, Stores ID */}
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Customer Name
                        </label>

                        <select
                          className="input-Provisioning"
                          value={customerid}
                          onChange={(e) => setCustomerid(e.target.value)}
                        >
                          <option value="">Select Customer</option>
                          {customerList.map((customer) => (
                            <option
                              key={customer.CustomerID}
                              value={customer.CustomerID}
                            >
                              {customer.CustomerName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">VIM Number</label>
                        <input
                          className="input-Provisioning"
                          value={vim}
                          onChange={(e) => setVim(e.target.value)}
                          placeholder="Enter VIM"
                        />
                      </div>
                      {/* <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Device ID</label>
                        <input
                          className="input-Provisioning"
                          value={deviceid}
                          onChange={(e) => setDeviceid(e.target.value)}
                          placeholder="Enter device ID"
                        />
                      </div> */}
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Device Type
                        </label>
                        <select
                          className="input-Provisioning"
                          value={deviceType}
                          onChange={(e) => setDeviceType(e.target.value)}
                        >
                          <option value="">Select Type</option>
                          <option value="TC1001">TC1001</option>
                          <option value="LCT001">LCT001</option>
                          <option value="AIS140">AIS140</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Registration Number
                        </label>
                        <input
                          className="input-Provisioning"
                          value={registrationNumber}
                          onChange={(e) =>
                            setRegistrationNumber(e.target.value)
                          }
                          placeholder="Enter Registration Number"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Registration Type
                        </label>
                        <select
                          className="input-Provisioning"
                          value={registrationType}
                          onChange={(e) => setRegistrationType(e.target.value)}
                        >
                          <option value="">Select Registration Type</option>
                          <option value="Individual">Individual</option>
                          <option value="Commercial">Commercial</option>
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Vehicle Type
                        </label>
                        <select
                          className="input-Provisioning"
                          value={vehicleType}
                          onChange={(e) => setVehicleType(e.target.value)}
                        >
                          <option value="">Select Vehicle Type</option>
                          <option value="2W">2W</option>
                          <option value="3W">3W</option>
                          <option value="4W">4W</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Powertrain</label>
                        <select
                          className="input-Provisioning"
                          value={powertrain}
                          onChange={(e) => setPowertrain(e.target.value)}
                        >
                          <option value="">Select Powertrain</option>
                          <option value="Electric">Electric</option>
                          <option value="Petrol">Petrol</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Vehicle IMEI
                        </label>
                        <input
                          className="input-Provisioning"
                          value={imei}
                          onChange={(e) => setImei(e.target.value)}
                          placeholder="Enter IMEI"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Vehicle IMSI
                        </label>
                        <input
                          className="input-Provisioning"
                          value={imsi}
                          onChange={(e) => setImsi(e.target.value)}
                          placeholder="Enter IMSI"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">ICCID</label>
                        <input
                          className="input-Provisioning"
                          value={iccid}
                          onChange={(e) => setIccid(e.target.value)}
                          placeholder="Enter ICCID"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Version</label>
                        <input
                          className="input-Provisioning"
                          value={version}
                          onChange={(e) => setVersion(e.target.value)}
                          placeholder="Enter Version"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Range (km)</label>
                        <input
                          className="input-Provisioning"
                          value={range}
                          onChange={(e) => setRange(e.target.value)}
                          placeholder="Enter Range"
                        />
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Operating Hours
                        </label>
                        <input
                          className="input-Provisioning"
                          value={hours}
                          onChange={(e) => setHours(e.target.value)}
                          placeholder="Enter Operating Hours"
                        />
                      </div>
                    </div>
                    {message && (
                      <div
                        style={{
                          color: isError ? "red" : "green",
                          textAlign: "center",
                          marginTop: "15px",
                        }}
                      >
                        {message}
                      </div>
                    )}
                    <div className="button-container-Provisioning">
                      <button
                        className="button-Provisioning-deviceassociate"
                        onClick={handleNavigate}
                      >
                        Device Associate
                      </button>
                      <button
                        className="button-Provisioning"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </>
                )}

                {/* --- TAB 2: ONBOARD DEVICE --- */}
                {activeTab === "onboard" && (
                  <>
                    <h2 className="title-Provisioning">Onboard Device</h2>
                    <div className="form-grid-Provisioning">
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">IMEI</label>
                        <input
                          className="input-Provisioning"
                          value={onboardImei}
                          onChange={(e) => setOnboardImei(e.target.value)}
                          placeholder="Enter IMEI"
                        />
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">IMSI</label>
                        <input
                          className="input-Provisioning"
                          value={onboardImsi}
                          onChange={(e) => setOnboardImsi(e.target.value)}
                          placeholder="Enter IMSI"
                        />
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">ICCID</label>
                        <input
                          className="input-Provisioning"
                          value={onboardIccid}
                          onChange={(e) => setOnboardIccid(e.target.value)}
                          placeholder="Enter ICCID"
                        />
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Type</label>

                        <select
                          className="input-Provisioning"
                          value={onboardDeviceType}
                          onChange={(e) => setOnboardDeviceType(e.target.value)}
                        >
                          <option value="">Select Type</option>
                          <option value="TC1001">TC1001</option>
                          <option value="LCT001">LCT001</option>
                          <option value="AIS140">AIS140</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Version</label>
                        <input
                          className="input-Provisioning"
                          value={onboardVersion}
                          onChange={(e) => setOnboardVersion(e.target.value)}
                          placeholder="Enter Version"
                        />
                      </div>

                      {/* UPDATED SELECT: Shows Name, Stores ID */}
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Customer Name
                        </label>
                        <select
                          className="input-Provisioning"
                          value={onboardCustomerId}
                          onChange={(e) => setOnboardCustomerId(e.target.value)}
                        >
                          <option value="">Select Customer</option>
                          {customerList.map((customer) => (
                            <option
                              key={customer.CustomerID}
                              value={customer.CustomerID}
                            >
                              {customer.CustomerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {onboardMessage && (
                      <div
                        style={{
                          color: isOnboardError ? "red" : "green",
                          textAlign: "center",
                          marginTop: "15px",
                        }}
                      >
                        {onboardMessage}
                      </div>
                    )}
                    <div className="button-container-Provisioning">
                      <button
                        className="button-Provisioning-deviceassociate"
                        onClick={handleNavigate}
                      >
                        Device Associate
                      </button>
                      <button
                        className="button-Provisioning"
                        onClick={handleOnboardSubmit}
                        disabled={isOnboarding}
                      >
                        {isOnboarding ? "Onboarding..." : "Submit"}
                      </button>
                    </div>
                  </>
                )}

                {/* --- TAB 3: ONBOARD VEHICLE --- */}
                {activeTab === "onboardVehicle" && (
                  <>
                    <h2 className="title-Provisioning">Onboard Vehicle</h2>
                    <div className="form-grid-Provisioning">
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Registration Number
                        </label>
                        <input
                          className="input-Provisioning"
                          value={onboardRegistrationNumber}
                          onChange={(e) =>
                            setOnboardRegistrationNumber(e.target.value)
                          }
                          placeholder="Enter Registration Number"
                        />
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">VIM Number</label>
                        <input
                          className="input-Provisioning"
                          value={onboardVim}
                          onChange={(e) => setOnboardVim(e.target.value)}
                          placeholder="Enter VIM"
                        />
                      </div>

                      {/* UPDATED SELECT: Shows Name, Stores ID */}
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Customer Name
                        </label>
                        <select
                          className="input-Provisioning"
                          value={onboardVehicleCustomerId}
                          onChange={(e) =>
                            setOnboardVehicleCustomerId(e.target.value)
                          }
                        >
                          <option value="">Select Customer</option>
                          {customerList.map((customer) => (
                            <option
                              key={customer.CustomerID}
                              value={customer.CustomerID}
                            >
                              {customer.CustomerName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Registration Type
                        </label>
                        <select
                          className="input-Provisioning"
                          value={onboardRegistrationType}
                          onChange={(e) =>
                            setOnboardRegistrationType(e.target.value)
                          }
                        >
                          <option value="">Select Registration Type</option>
                          <option value="Individual">Individual</option>
                          <option value="Commercial">Commercial</option>
                        </select>
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Powertrain</label>
                        <select
                          className="input-Provisioning"
                          value={onboardVehiclePowertrain}
                          onChange={(e) =>
                            setOnboardVehiclePowertrain(e.target.value)
                          }
                        >
                          <option value="">Select Powertrain</option>
                          <option value="Electric">Electric</option>
                          <option value="Petrol">Petrol</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Vehicle Type
                        </label>
                        <select
                          className="input-Provisioning"
                          value={onboardVehicleType}
                          onChange={(e) =>
                            setOnboardVehicleType(e.target.value)
                          }
                        >
                          <option value="">Select Vehicle Type</option>
                          <option value="2W">2W</option>
                          <option value="3W">3W</option>
                          <option value="4W">4W</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">Range (km)</label>
                        <input
                          className="input-Provisioning"
                          value={onboardVehicleRange}
                          onChange={(e) =>
                            setOnboardVehicleRange(e.target.value)
                          }
                          placeholder="Enter Range in km"
                        />
                      </div>
                      <div className="form-group-Provisioning">
                        <label className="label-Provisioning">
                          Operating Hours
                        </label>
                        <input
                          className="input-Provisioning"
                          value={onboardVehicleHours}
                          onChange={(e) =>
                            setOnboardVehicleHours(e.target.value)
                          }
                          placeholder="Enter Operating Hours"
                        />
                      </div>
                    </div>
                    {onboardVehicleMessage && (
                      <div
                        style={{
                          color: isOnboardVehicleError ? "red" : "green",
                          textAlign: "center",
                          marginTop: "15px",
                        }}
                      >
                        {onboardVehicleMessage}
                      </div>
                    )}
                    <div className="button-container-Provisioning">
                      <button
                        className="button-Provisioning-deviceassociate"
                        onClick={handleNavigate}
                      >
                        Device Associate
                      </button>
                      <button
                        className="button-Provisioning"
                        onClick={handleVehicleOnboardSubmit}
                        disabled={isVehicleOnboarding}
                      >
                        {isVehicleOnboarding ? "Onboarding..." : "Submit"}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
      <div className="device-associate-wrapper">
        <Deviceassociate />
      </div>
    </div>
  );
}
